import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { BOARD_LABELS } from '../data/curriculumData';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(val) {
  if (!val) return null;
  try {
    const d = typeof val.toDate === 'function' ? val.toDate() : new Date(val);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return null; }
}

function isDueOverdue(dueDate) {
  if (!dueDate) return false;
  try { return new Date(dueDate) < new Date(); } catch { return false; }
}

export default function StudentCurriculum({ teacherId, teacherName, schoolName, allTasks = [], allSubmissions = [], onBack, onTaskSelect }) {
  const [curricula, setCurricula] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [activeGrade, setActiveGrade] = useState(null);

  // Group tasks by subject + chapter
  const tasksByChapter = {};
  allTasks.forEach((t) => {
    const key = `${t.subjectName}_${t.chapterName}`;
    if (!tasksByChapter[key]) tasksByChapter[key] = [];
    tasksByChapter[key].push(t);
  });

  const getTaskStatus = (taskId) => {
    const sub = allSubmissions.find((s) => s.taskId === taskId);
    return sub ? 'submitted' : 'pending';
  };

  // Fetch curricula
  useEffect(() => {
    if (!teacherId) return;
    const fetch = async () => {
      try {
        const q = query(collection(db, 'teacherCurricula'), where('teacherId', '==', teacherId));
        const snap = await getDocs(q);
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.grade || 0) - (b.grade || 0));
        setCurricula(list);
        if (list.length > 0) setActiveGrade(list[0].grade);
      } catch (err) { console.error('Fetch curricula error:', err); }
      setLoading(false);
    };
    fetch();
  }, [teacherId]);

  const toggleSubject = (currId, subjId) => {
    setExpanded((prev) => ({
      ...prev,
      [currId]: { ...(prev[currId] || {}), [subjId]: !(prev[currId]?.[subjId]) },
    }));
  };

  const gradeCurricula = curricula.filter((c) => c.grade === activeGrade);
  const uniqueGrades = [...new Set(curricula.map((c) => c.grade))];
  const totalTasks = allTasks.length;
  const doneTasks = allSubmissions.length;

  return (
    <div className="dashboard-wrapper">
      {/* ── Premium Header ── */}
      <header className="dashboard-header">
        <div className="header-left">
          <div className="student-avatar" style={{ width: 44, height: 44, fontSize: '18px', borderRadius: '12px', background: 'var(--gradient-primary)', color: '#fff', flexShrink: 0 }}>
            {schoolName?.charAt(0) || '🏫'}
          </div>
          <div className="header-info">
            <h2 className="header-title">{schoolName}</h2>
            <p className="header-subtitle">Teacher: {teacherName}</p>
          </div>
        </div>
        <button className="btn-outline btn-outline--sm" onClick={onBack}>← Back</button>
      </header>

      {/* ── Stats Row ── */}
      {!loading && curricula.length > 0 && (
        <div className="stats-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginTop: 16 }}>
          <div className="stat-card" style={{ cursor: 'default' }}>
            <div className="stat-num">{curricula.length}</div>
            <div className="stat-label">Curricula</div>
          </div>
          <div className="stat-card" style={{ cursor: 'default' }}>
            <div className="stat-num">{totalTasks}</div>
            <div className="stat-label">My Tasks</div>
          </div>
          <div className="stat-card" style={{ cursor: 'default' }}>
            <div className="stat-num" style={{ color: 'var(--success)' }}>{doneTasks}</div>
            <div className="stat-label">Completed</div>
          </div>
        </div>
      )}

      {/* ── Loading Skeleton ── */}
      {loading && (
        <div className="animate-in" style={{ marginTop: 24 }}>
          <div className="skeleton" style={{ height: 44, width: 260, marginBottom: 20 }}></div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton" style={{ height: 64, borderRadius: 'var(--radius)', marginBottom: 10 }}></div>
          ))}
        </div>
      )}

      {/* ── Empty State ── */}
      {!loading && curricula.length === 0 && (
        <div className="empty-state" style={{ marginTop: 32 }}>
          <span>📭</span>
          <p>Your teacher hasn't published any curriculum yet. Check back soon!</p>
        </div>
      )}

      {/* ── Curriculum Content ── */}
      {!loading && curricula.length > 0 && (
        <>
          {/* Grade Tabs */}
          {uniqueGrades.length > 1 && (
            <div className="grade-tabs">
              {uniqueGrades.map((g) => (
                <button
                  key={g}
                  className={`grade-tab ${activeGrade === g ? 'grade-tab--active' : ''}`}
                  onClick={() => setActiveGrade(g)}
                >
                  Class {g}
                </button>
              ))}
            </div>
          )}

          {/* Curriculum Cards */}
          <div className="animate-in">
            {gradeCurricula.map((curr) => (
              <div key={curr.id} className="section">
                <div className="section-header-row" style={{ marginBottom: 12 }}>
                  <h3 className="section-title" style={{ marginBottom: 0 }}>
                    📘 {curr.name}
                  </h3>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {curr.sourceBoard
                      ? <span className="chip chip--sm chip--outline">{BOARD_LABELS[curr.sourceBoard] || curr.sourceBoard}</span>
                      : <span className="chip chip--sm chip--outline">Custom</span>
                    }
                  </div>
                </div>

                {/* Subjects accordion */}
                <div className="subjects-view">
                  {(curr.subjects || []).map((subj, si) => (
                    <div
                      key={subj.id}
                      className="subject-view-block animate-in"
                      style={{ animationDelay: `${si * 0.08}s` }}
                    >
                      <button
                        className="subject-view-header"
                        onClick={() => toggleSubject(curr.id, subj.id)}
                      >
                        <span className="subject-view-num">{si + 1}</span>
                        <span className="subject-view-name">{subj.name}</span>
                        <span className="subject-view-count">{subj.chapters?.length || 0} chapters</span>
                        <span className="cv-chevron" style={{ transform: expanded[curr.id]?.[subj.id] ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>›</span>
                      </button>

                      {expanded[curr.id]?.[subj.id] && (
                        <div className="chapter-view-list">
                          {(subj.chapters || []).map((ch, ci) => {
                            const chTasks = tasksByChapter[`${subj.name}_${ch.title}`] || [];
                            return (
                              <div key={ch.id} className="chapter-view-item chapter-view-item--complex">
                                <div className="chapter-view-row">
                                  <span className="chapter-view-num">{ci + 1}</span>
                                  <span className="chapter-view-title">{ch.title}</span>
                                  {chTasks.length > 0 && (
                                    <span className="chip chip--sm" style={{ marginLeft: 'auto' }}>
                                      {chTasks.filter(t => getTaskStatus(t.id) === 'submitted').length}/{chTasks.length} done
                                    </span>
                                  )}
                                </div>

                                {chTasks.length > 0 && (
                                  <div className="chapter-tasks">
                                    {chTasks.map((t, ti) => {
                                      const status = getTaskStatus(t.id);
                                      const overdue = isDueOverdue(t.dueDate);
                                      return (
                                        <div
                                          key={t.id}
                                          className={`task-row task-row--${status} animate-in`}
                                          style={{ animationDelay: `${ti * 0.05}s` }}
                                          onClick={() => onTaskSelect?.(t)}
                                          role="button"
                                          tabIndex={0}
                                          onKeyDown={(e) => e.key === 'Enter' && onTaskSelect?.(t)}
                                        >
                                          <div className="task-row-left">
                                            <span className="task-icon">{status === 'submitted' ? '✅' : '📋'}</span>
                                            <div className="task-info">
                                              <span className="task-title">{t.title}</span>
                                              <span className="task-meta">
                                                {t.dueDate ? `Due ${formatDate(t.dueDate)}` : 'No due date'}
                                                {status === 'pending' && overdue && (
                                                  <span className="task-tag task-tag--overdue">Overdue</span>
                                                )}
                                              </span>
                                            </div>
                                          </div>
                                          <div className="task-row-right">
                                            <span className={`status-tag status-tag--${status}`}>
                                              {status === 'submitted' ? 'Done' : 'Open'}
                                            </span>
                                            <span className="task-arrow">›</span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {subj.chapters?.length === 0 && (
                            <p className="chapter-empty">No chapters added yet.</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
